import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, UserPlus, AlertTriangle } from 'lucide-react';
import { createMember, getMemberships, Membership, DuplicateMatch } from '../api';
import { useToast } from '../../../context/ToastContext';
import { ApiError } from '../../../services/apiClient';

const inputCls =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';
const labelCls = 'mb-1 block text-xs font-medium uppercase tracking-wide text-content-3';

// (31-ago-2026) El selector de "Rol" se RETIRÓ: este formulario SOLO da de alta alumnos
// (el backend siempre ignoró el rol — "Profesor" aquí creaba una alumna). Profesores se
// dan de alta en /profesores y el staff con acceso en /usuarios.

/** Modal de alta de un nuevo miembro. onCreated(id) recibe el account_id creado. */
export default function MemberForm({ onClose, onCreated }: { onClose: () => void; onCreated: (id: number) => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  // Antiduplicados (31-ago-2026, pedido de Karla): el backend contesta 409 con las
  // coincidencias (teléfono o nombre, activas e inactivas); aquí se muestran y la
  // persona decide: abrir la ficha existente o crear de todos modos (homónimas reales).
  const [dups, setDups] = useState<DuplicateMatch[] | null>(null);
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [prefix, setPrefix] = useState('52');
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState('');
  const [membershipId, setMembershipId] = useState('');
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { getMemberships().then((r) => setMemberships(r.data)).catch(() => {}); }, []);
  // Si corrigen nombre o teléfono tras el aviso, el aviso deja de aplicar: se limpia y
  // el botón vuelve a ser el alta normal (que re-verifica en el servidor).
  useEffect(() => { setDups(null); }, [firstname, lastname, phone]);
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  async function submit(force = false) {
    if (!firstname.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      const r = await createMember({
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        email: email.trim() || null,
        phone_prefix: phone.trim() ? prefix.trim() : null,
        phone_number: phone.trim() || null,
        dob: dob || null,
        membership_id: membershipId ? Number(membershipId) : null,
        force_duplicate: force || undefined,
      });
      toast.success('Alumno dado de alta');
      onCreated(r.data.account_id);
    } catch (e: any) {
      const err = e as ApiError;
      if (err?.status === 409 && Array.isArray(err.data?.duplicates)) {
        setDups(err.data.duplicates as DuplicateMatch[]);
      } else {
        toast.error(e?.message || 'No se pudo crear');
      }
    } finally { setSaving(false); }
  }

  return createPortal(
    <div className="modal-overlay fixed inset-0 z-[200] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div
        className="modal-panel max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-line bg-surface shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 flex items-center justify-between border-b border-line bg-surface px-5 py-3.5">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold text-content"><UserPlus size={18} className="text-brand-blue" /> Nuevo alumno</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-content-3 transition hover:bg-surface-2 hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>

        <div className="space-y-4 p-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Nombre *</label>
              <input className={inputCls} value={firstname} onChange={(e) => setFirstname(e.target.value)} autoFocus placeholder="Nombre" />
            </div>
            <div>
              <label className={labelCls}>Apellidos</label>
              <input className={inputCls} value={lastname} onChange={(e) => setLastname(e.target.value)} placeholder="Apellidos" />
            </div>
          </div>

          <div>
            <label className={labelCls}>Correo</label>
            <input className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="correo@ejemplo.com (opcional)" />
          </div>

          <div>
            <label className={labelCls}>Teléfono</label>
            <div className="flex gap-2">
              {/* 13-oct-2026 (Mabel): `inputCls` trae w-full y le ganaba a w-16 → el PREFIJO ocupaba todo el
                  ancho y el teléfono quedaba como un cuadrito fuera del modal; escribían el número en el
                  prefijo y el alta se guardaba SIN teléfono. Ancho fijo con !w-16 y el teléfono flex-1. */}
              <input className={`${inputCls} !w-16 shrink-0 text-center`} value={prefix} maxLength={3} onChange={(e) => setPrefix(e.target.value.replace(/\D/g, '').slice(0, 3))} aria-label="Prefijo" />
              <input className={`${inputCls} min-w-0 flex-1`} value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))} placeholder="10 dígitos (opcional)" inputMode="numeric" />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Fecha de nacimiento</label>
              <input className={inputCls} type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Mensualidad</label>
              <select className={inputCls} value={membershipId} onChange={(e) => setMembershipId(e.target.value)}>
                <option value="">— Sin mensualidad —</option>
                {memberships.map((m) => <option key={m.membership_id} value={m.membership_id}>{m.name}</option>)}
              </select>
            </div>
          </div>

          <p className="text-xs text-content-3">
            Aquí solo se dan de alta <b>alumnos</b>. Los profesores se registran en la pantalla
            <b> Profesores</b>, y el personal con acceso al sistema en <b>Usuarios</b>.
          </p>

          {dups && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-600 dark:text-amber-400">
                <AlertTriangle size={15} /> Puede que ya exista
              </p>
              <ul className="mt-2 space-y-1.5">
                {dups.map((d) => (
                  <li key={d.account_id} className="flex items-center justify-between gap-2 text-sm text-content">
                    <span>
                      {d.name}
                      {d.phone ? <span className="text-content-3"> · {d.phone}</span> : null}
                      {!d.is_active && (
                        <span className="ml-1.5 rounded bg-canvas px-1.5 py-0.5 text-[10px] font-semibold uppercase text-content-3 border border-line">Inactiva</span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => { onClose(); navigate(`/alumnos/${d.account_id}`); }}
                      className="shrink-0 rounded-lg border border-line bg-surface px-2.5 py-1 text-xs font-medium text-brand-blue transition hover:bg-surface-2"
                    >
                      Abrir ficha
                    </button>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-content-3">
                Si es una persona inactiva, mejor <b>reactívala desde su ficha</b> (conserva su historial y sus pagos).
                Solo crea una ficha nueva si de verdad es otra persona.
              </p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 flex justify-end gap-2 border-t border-line bg-surface px-5 py-3.5">
          <button onClick={onClose} className="rounded-lg border border-line bg-surface px-4 py-2 text-sm font-medium text-content-2 transition hover:bg-surface-2">Cancelar</button>
          {dups ? (
            <button onClick={() => submit(true)} disabled={saving}
              className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-2 text-sm font-semibold text-amber-600 dark:text-amber-400 transition hover:bg-amber-500/20 disabled:opacity-40">
              {saving ? 'Creando…' : 'Es otra persona, crear de todos modos'}
            </button>
          ) : (
            <button onClick={() => submit()} disabled={saving || !firstname.trim()}
              className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:opacity-40">
              {saving ? 'Creando…' : 'Crear alumno'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
